import { NgClass } from "@angular/common";
import { Component, inject, input, type OnDestroy, type OnInit } from "@angular/core";
import { AlertService, ButtonIcon, ProgressSpinner } from "@ui";
import type { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import type { Corpus, CorpusWithTransforms } from "@pienso/ingest-types/corpus.interface";
import { Status, type StatusResponse } from "@pienso/ingest-types/task.interface";
import type { TRANSFORM_TYPE, Transform, TransformWithCorpus } from "@pienso/ingest-types/transform.interface";
import { CorpusDataService } from "../services/corpus-data.service";
import { TransformerService } from "../transform/transformer.service";
import { TransformerRegistryService } from "../transform/transformer-registry.service";

@Component({
  selector: "app-transform-history",
  templateUrl: "./transform-history.component.html",
  styleUrls: ["./transform-history.component.scss"],
  imports: [ProgressSpinner, NgClass, ButtonIcon],
})
export class TransformHistoryComponent implements OnInit, OnDestroy {
  service = inject(TransformerService);
  registry = inject(TransformerRegistryService);
  corpusService = inject(CorpusDataService);
  private alertService = inject(AlertService);

  readonly corpus = input.required<Corpus>();

  readonly editable = input(true);

  transforms: TransformWithCorpus[] | undefined;

  isPending = false;
  isLoading = false;

  activeTransform: Transform | undefined;

  private subscriptions: Subscription[] = [];
  private xStateSubscription: Subscription | undefined;

  ngOnInit() {
    this.unsubscribe();
    this.subscriptions.push(
      this.service.watchCorpusTransforms(this.corpus().id).subscribe((responses: StatusResponse[]) => {
        let pending = false,
          done = !responses.length;
        responses.forEach((sr) => {
          pending = pending || sr.status === Status.PENDING || sr.status === Status.PROCESSING;
          done = done || sr.status === Status.COMPLETE;
        });
        this.isPending = pending;
        if (done) {
          this.loadTransforms();
        }
      }),
      this.service.activeTransformer$.subscribe((t) => {
        if (this.xStateSubscription) {
          this.xStateSubscription.unsubscribe();
        }
        if (t) {
          // state$ is a behaviorSubject, so will emit the current state immediately
          this.xStateSubscription = t.service.state$.subscribe((s) => {
            this.activeTransform = t.transform;
          });
        } else {
          this.activeTransform = undefined;
        }
      }),
    );
    this.loadTransforms();
  }

  loadTransforms() {
    this.isLoading = true;
    this.corpusService
      .transformHistory(this.corpus().id)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe((corpus: CorpusWithTransforms) => {
        this.transforms = corpus.transforms.map((t) => {
          t.corpus = corpus;
          return <TransformWithCorpus>t;
        });
        if (
          this.service.activeTransformer?.transform &&
          !this.transforms.find((t) => t.id === this.service.activeTransformer!.transform!.id)
        ) {
          this.service.reset();
        }
      }, (e) => this.alertService.error(e));
  }

  ngOnDestroy() {
    this.unsubscribe();
  }

  private unsubscribe() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
    if (this.xStateSubscription) {
      this.xStateSubscription.unsubscribe();
    }
  }

  public describeTransform(xform: Transform) {
    const transformer = this.registry.transformerForType(xform.transformType as TRANSFORM_TYPE);
    return transformer ? transformer.describe(xform) : "Unknown transformation";
  }

  editTransform(xform: TransformWithCorpus) {
    if (!this.editable()) {
      return;
    }
    this.service.editTransform(xform);
  }

  deleteTransform(e: Event, xform: Transform) {
    // Prevents the delete click from triggering the edit
    e.stopPropagation();

    this.isLoading = true;
    this.service
      .deleteTransform(xform)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((corpus: CorpusWithTransforms) => {}, (e) => this.alertService.error(e));
  }
}
